import Axios from 'axios';
import { setupCache } from 'axios-cache-interceptor';
import { PasswordIsInvalidError, UsernameIsInvalidError } from './types/exceptions';
import { PaymentRequest } from './types/payment';
import { ContactFormState } from './components/contact/schema';
import { NoticeRequestBody, PublicNotice } from './types/notices';
import { PublicEvent } from './types/events';
import { EventInviteFormState } from './pages/forms/event-invite/schema';
import { NewTerminationNotice, TerminationNotice } from './pages/y-saatio/termination/schema';
import { Form } from './types/forms';
import { InitiateImpersonation } from './pages/admin/impersonate/schema';

const SESSION_ID_KEY = 'sessionId';

const instance = Axios.create();
const axios = setupCache(instance);

// Recover session ID from localStorage
const sessionId = window.localStorage.getItem(SESSION_ID_KEY);
if (sessionId) {
  setSessionId(sessionId);
}

// M2 Kodit Backend endpoints
const backendUrl = process.env.REACT_APP_BACKEND_URL;
const REGISTER_INITIATE_ENDPOINT = backendUrl + '/register/initiate';
const REGISTER_CREDENTIALS_ENDPOINT = backendUrl + '/register/credentials';
const REGISTER_OTP_ENDPOINT = backendUrl + '/register/confirmation-code';
const REQUEST_CONFIRMATION_CODE_ENDPOINT = backendUrl + '/request-confirmation-code';
const LOGIN_ENDPOINT = backendUrl + '/login';
const LOGOUT_ENDPOINT = backendUrl + '/logout';
const PROFILE_ENDPOINT = backendUrl + '/profile';
const BILLING_HISTORY_ENDPOINT = backendUrl + '/billing/history';
const CURRENT_BILL_ENDPOINT = backendUrl + '/billing/current';
const CURRENT_TARGETS_ENDPOINT = backendUrl + '/billing/targets';
const DEBT_ENDPOINT = backendUrl + '/billing/debt';
const SECURITY_DEPOSIT_ENDPOINT = backendUrl + '/billing/security-deposit';
const CURRENT_CONTRACT_ENDPOINT = backendUrl + '/contracts/current';
const CONTRACT_TENANTS_ENDPOINT = backendUrl + '/contracts/tenants';
const APARTMENT_ENDPOINT = backendUrl + '/apartment';
const FORGOT_PASSWORD_CODE_ENDPOINT = backendUrl + '/forgot-password/code';
const FORGOT_PASSWORD_RESET_ENDPOINT = backendUrl + '/forgot-password/reset';
const BUILDING_ENDPOINT = backendUrl + '/building';
const DOCUMENTS_ENDPOINT = backendUrl + '/documents';
const PAYMENT_ENDPOINT = backendUrl + '/payment';
const CONTACT_REQUEST_ENDPOINT = backendUrl + '/contact-request';
const EVENTS_ENDPOINT = backendUrl + '/events';
const EVENTS_CREATE_ENDPOINT = EVENTS_ENDPOINT + '/create';
const EVENTS_SUBMIT_ENDPOINT = EVENTS_ENDPOINT + '/submit';
const EVENTS_UPDATE_ENDPOINT = EVENTS_ENDPOINT + '/update';
const NOTICES_ENDPOINT = backendUrl + '/notices';
const NOTICES_CREATE_ENDPOINT = NOTICES_ENDPOINT + '/create';
const NOTICES_SUBMIT_ENDPOINT = NOTICES_ENDPOINT + '/submit';
const NOTICES_UPDATE_ENDPOINT = NOTICES_ENDPOINT + '/update';
const EXPENSE_REPORT_ENDPOINT = backendUrl + '/expense-reports';
const EXPENSE_REPORT_CREATE_ENDPOINT = EXPENSE_REPORT_ENDPOINT + '/create';
const EXPENSE_REPORT_SUBMIT_ENDPOINT = EXPENSE_REPORT_ENDPOINT + '/submit';
const EXPENSE_REPORT_UPDATE_ENDPOINT = EXPENSE_REPORT_ENDPOINT + '/update';
const CONTRACT_TERMINATION_ENDPOINT = backendUrl + '/contract-termination';
const CREATE_CONTRACT_TERMINATION_ENDPOINT = backendUrl + '/contract-termination/create';
const ACCEPT_CONTRACT_TERMINATION_ENDPOINT = backendUrl + '/contract-termination/approval/accept';
const REJECT_CONTRACT_TERMINATION_ENDPOINT = backendUrl + '/contract-termination/approval/reject';
const KENNO_NOTIFICATIONS_ENDPOINT = backendUrl + '/notifications';
const FORMS_ENDPOINT = backendUrl + '/forms';
const FORM_REVIEW_ENDPOINT = backendUrl + '/form-review';
const FORM_APPROVAL_ENDPOINT = FORM_REVIEW_ENDPOINT + '/approve';
const FORM_REJECTION_ENDPOINT = FORM_REVIEW_ENDPOINT + '/reject';
const FORM_REVIEW_RESEND_TOKEN = FORM_REVIEW_ENDPOINT + '/resend';
const DEFECT_NOTICE_CREATE_ENDPOINT = backendUrl + '/defect-notices/create';
const IMPERSONATE_INIT_ENDPOINT = backendUrl + '/impersonate/initiate';
const IMPERSONATE_LOGIN_ENDPOINT = backendUrl + '/impersonate/login';

export type LoginCredentials = {
  username: string;
  password: string;
};

function setSessionId(sessionId: string) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${sessionId}`;
  window.localStorage.setItem(SESSION_ID_KEY, sessionId);
}

function clearSessionId() {
  delete axios.defaults.headers.common['Authorization'];
  window.localStorage.removeItem(SESSION_ID_KEY);
}

export async function login(credentials: { username: string; password: string }) {
  const response = await axios.post(LOGIN_ENDPOINT, credentials, {
    withCredentials: true,
    cache: false,
  });
  const data = response.data;
  // NOTE: in the near future this will be mandatory and will throw an error if
  // not found. For now it's optional for version compatibility.
  if (data.sessionId) {
    setSessionId(data.sessionId);
  }
}

export async function logout() {
  try {
    await axios.post(
      LOGOUT_ENDPOINT,
      {},
      {
        withCredentials: true,
        cache: false,
      }
    );
  } catch {
  } finally {
    clearSessionId();
    if (axios.storage?.clear) {
      await axios.storage.clear();
    }
  }
}

export async function initiateStrongAuthentication(data?: any): Promise<{ url: string; state: string }> {
  const response = await axios.post(REGISTER_INITIATE_ENDPOINT, data);
  if (response.status === 200) {
    const { url, state } = response.data;
    return { url, state };
  }
  throw new Error('Failed to initiate registration');
}

export async function submitRegistrationCredentials(
  username: string,
  password: string,
  state: string,
  registerSessionId: string
): Promise<{ nextStep: string }> {
  try {
    const response = await axios.post(
      REGISTER_CREDENTIALS_ENDPOINT,
      { username, password, state, registerSessionId },
      { withCredentials: true }
    );
    if (response.status === 200) {
      const { nextStep } = response.data;
      return { nextStep };
    }
  } catch (error: any) {
    if (error.response.status === 409) {
      throw new UsernameIsInvalidError();
    }
    if (error.response.status === 422) {
      throw new PasswordIsInvalidError();
    }
  }
  throw new Error('Failed to submit credentials');
}

export async function submitRegistrationConfirmationCode(
  confirmationCode: string,
  username: string
): Promise<{ nextStep: string }> {
  const response = await axios.post(REGISTER_OTP_ENDPOINT, { confirmationCode, username }, { withCredentials: true });
  if (response.status === 200) {
    const { nextStep } = response.data;
    return { nextStep };
  }
  throw new Error('Failed to submit confirmation code');
}

export async function requestConfirmationCode(username: string): Promise<void> {
  const response = await axios.post(REQUEST_CONFIRMATION_CODE_ENDPOINT, { username }, { withCredentials: true });
  if (response.status === 200) {
    return;
  }
  throw new Error('Failed to request new confirmation code');
}

export async function getProfile() {
  const response = await axios.get(PROFILE_ENDPOINT, {
    withCredentials: true,
  });
  if (response.status === 200) {
    return response.data;
  }
  throw new Error('Failed to get profile');
}

export async function getBillingHistory() {
  const response = await axios.get(BILLING_HISTORY_ENDPOINT, {
    withCredentials: true,
  });
  if (response.status === 200) {
    return response.data;
  }
  throw new Error('Failed to get billing history');
}

export async function getCurrentBill() {
  const response = await axios.get(CURRENT_BILL_ENDPOINT, {
    withCredentials: true,
  });
  if (response.status === 200) {
    return response.data;
  }
  throw new Error('Failed to get current bill');
}

export async function getCurrentTargets() {
  const response = await axios.get(CURRENT_TARGETS_ENDPOINT, {
    withCredentials: true,
  });
  if (response.status === 200) {
    return response.data;
  }
  throw new Error('Failed to get current targets');
}

export async function getDebt() {
  const response = await axios.get(DEBT_ENDPOINT, {
    withCredentials: true,
  });
  if (response.status === 200) {
    return response.data;
  }
  throw new Error('Failed to get debt');
}

export async function getSecurityDeposit() {
  const response = await axios.get(SECURITY_DEPOSIT_ENDPOINT, {
    withCredentials: true,
  });
  if (response.status === 200) {
    return response.data;
  }
  throw new Error('Failed to get security deposit');
}

export async function getCurrentContract() {
  const response = await axios.get(CURRENT_CONTRACT_ENDPOINT, {
    withCredentials: true,
  });
  if (response.status === 200) {
    return response.data;
  }
  throw new Error('Failed to get current contract');
}

export async function getContractTenants() {
  const response = await axios.get(CONTRACT_TENANTS_ENDPOINT, {
    withCredentials: true,
  });
  if (response.status === 200) {
    return response.data;
  }
  throw new Error('Failed to get contract tenants');
}

export async function getApartment() {
  const response = await axios.get(APARTMENT_ENDPOINT, {
    withCredentials: true,
  });
  if (response.status === 200) {
    return response.data;
  }
  throw new Error('Failed to get the apartment');
}

export async function sendResetPasswordCode(data: { username: string }) {
  const response = await axios.post(FORGOT_PASSWORD_CODE_ENDPOINT, data);
  if (response.status === 200) {
    return;
  }
  throw new Error('Failed to send reset password code to user');
}

export async function confirmNewPassword(data: { username: string; code: string; newPassword: string }) {
  const response = await axios.post(FORGOT_PASSWORD_RESET_ENDPOINT, data);
  if (response.status === 200) {
    return;
  }
  throw new Error('Failed to confirm new password');
}

export async function getBuilding() {
  const response = await axios.get(BUILDING_ENDPOINT, {
    withCredentials: true,
  });
  if (response.status === 200) {
    return response.data;
  }
  throw new Error('Failed to get building');
}

export async function getDocuments() {
  const response = await axios.get(DOCUMENTS_ENDPOINT, {
    withCredentials: true,
  });
  if (response.status === 200) {
    return response.data;
  }
  throw new Error('Failed to get documents');
}

export async function getPublicNotices(): Promise<PublicNotice[]> {
  const response = await axios.get(NOTICES_ENDPOINT, {
    withCredentials: true,
    cache: false,
  });
  if (response.status === 200) {
    return response.data.data;
  }
  throw new Error('Failed to get public notices');
}

export async function createNotice(body: NoticeRequestBody): Promise<{ id: string }> {
  const response = await axios.post(NOTICES_CREATE_ENDPOINT, body, {
    withCredentials: true,
  });
  if (response.status === 201) {
    return response.data.data;
  }
  throw new Error('Failed to create notice');
}

export async function publishNotice(id: string) {
  const response = await axios.patch(`${NOTICES_SUBMIT_ENDPOINT}/${id}`, {
    withCredentials: true,
  });
  if (response.status === 200) {
    return;
  }
  throw new Error('Failed to publish notice');
}

export async function updateNotice(id: string, body: NoticeRequestBody) {
  const response = await axios.put(`${NOTICES_UPDATE_ENDPOINT}/${id}`, body, {
    withCredentials: true,
  });
  if (response.status === 200) {
    return;
  }
  throw new Error('Failed to update notice');
}

export async function getPublicEvents(): Promise<PublicEvent[]> {
  const response = await axios.get(EVENTS_ENDPOINT, {
    withCredentials: true,
    cache: false,
  });
  if (response.status === 200) {
    return response.data.data;
  }
  throw new Error('Failed to get public events');
}

export async function createEvent(formData: EventInviteFormState): Promise<{ id: string }> {
  const body = { formData };
  const response = await axios.post(EVENTS_CREATE_ENDPOINT, body, {
    withCredentials: true,
  });
  if (response.status === 201) {
    return response.data.data;
  }
  throw new Error('Failed to create event invite');
}

export async function publishEvent(id: string) {
  const response = await axios.patch(`${EVENTS_SUBMIT_ENDPOINT}/${id}`, {
    withCredentials: true,
  });
  if (response.status === 200) {
    return;
  }
  throw new Error('Failed to publish event invite');
}

export async function updateEvent(id: string, formData: EventInviteFormState) {
  const body = { formData };
  const response = await axios.put(`${EVENTS_UPDATE_ENDPOINT}/${id}`, body, {
    withCredentials: true,
  });
  if (response.status === 200) {
    return;
  }
  throw new Error('Failed to update event invite');
}

export async function createExpenseReport(formData: FormData): Promise<{ id: string }> {
  const response = await axios.post(EXPENSE_REPORT_CREATE_ENDPOINT, formData, {
    withCredentials: true,
  });
  if (response.status === 201) {
    return response.data.data;
  }
  throw new Error('Failed to create expense report');
}

export async function submitExpenseReport(id: string) {
  const response = await axios.patch(`${EXPENSE_REPORT_SUBMIT_ENDPOINT}/${id}`, {
    withCredentials: true,
  });
  if (response.status === 200) {
    return;
  }
  throw new Error('Failed to submit expense report');
}

export async function updateExpenseReport(id: string, formData: FormData) {
  const response = await axios.put(`${EXPENSE_REPORT_UPDATE_ENDPOINT}/${id}`, formData, {
    withCredentials: true,
  });
  if (response.status === 200) {
    return;
  }
  throw new Error('Failed to update expense report');
}

export async function getOwnForms(): Promise<Form[]> {
  const response = await axios.get(FORMS_ENDPOINT, {
    withCredentials: true,
    cache: false,
  });
  if (response.status === 200) {
    return response.data.data;
  }
  throw new Error('Failed to get own forms');
}

export async function getFormByToken(token: string) {
  const response = await axios.get(`${FORM_REVIEW_ENDPOINT}/${token}`);
  if (response.status === 200) {
    return response.data.data;
  }
  throw new Error('Failed to get form by token');
}

export async function approveFormByToken(token: string) {
  const response = await axios.patch(`${FORM_APPROVAL_ENDPOINT}/${token}`);
  if (response.status === 200) {
    return;
  }
  throw new Error('Failed to approve form by token');
}

export async function rejectFormByToken(token: string) {
  const response = await axios.patch(`${FORM_REJECTION_ENDPOINT}/${token}`);
  if (response.status === 200) {
    return;
  }
  throw new Error('Failed to reject form by token');
}

export async function resendToken(oldToken: string) {
  const response = await axios.post(`${FORM_REVIEW_RESEND_TOKEN}/${oldToken}`);
  if (response.status === 200) {
    return;
  }
  throw new Error('Failed to send new token');
}

export async function createPayment(body: PaymentRequest) {
  const response = await axios.post(PAYMENT_ENDPOINT, body, {
    withCredentials: true,
  });
  if (response.status === 201) {
    return response.data;
  }
  throw new Error('Failed to create payment');
}

export async function postContactRequest(body: ContactFormState) {
  const response = await axios.post(CONTACT_REQUEST_ENDPOINT, body, {
    withCredentials: true,
  });
  if (response.status === 200) {
    return;
  }
  throw new Error('Failed to post contact request');
}

export async function initializeTerminationNotice(formData: NewTerminationNotice): Promise<string> {
  const response = await axios.post(CREATE_CONTRACT_TERMINATION_ENDPOINT, { formData }, { withCredentials: true });
  if (response.status === 200) {
    return response.data.url;
  }
  throw new Error('Failed to submit termination notice');
}

export async function getTerminationNotice(): Promise<TerminationNotice> {
  const response = await axios.get(CONTRACT_TERMINATION_ENDPOINT, {
    withCredentials: true,
    cache: false,
  });
  if (response.status === 200) {
    return response.data;
  }
  throw new Error('Failed to get termination notice');
}

export async function acceptTerminationNotice(id: number): Promise<string> {
  const response = await axios.post(`${ACCEPT_CONTRACT_TERMINATION_ENDPOINT}/${id}`, {
    withCredentials: true,
  });
  if (response.status === 200) {
    return response.data.url;
  }
  throw new Error('Failed to approve termination notice');
}

export async function rejectTerminationNotice(id: number): Promise<string> {
  const response = await axios.post(`${REJECT_CONTRACT_TERMINATION_ENDPOINT}/${id}`, {
    withCredentials: true,
  });
  if (response.status === 200) {
    return response.data.url;
  }
  throw new Error('Failed to reject termination notice');
}

export async function getKennoNotifications() {
  const response = await axios.get(KENNO_NOTIFICATIONS_ENDPOINT, {
    withCredentials: true,
  });
  if (response.status === 200) {
    return response.data;
  }
  throw new Error('Failed to fetch Kenno notifications');
}

export async function createDefectNotice(formData: FormData) {
  const response = await axios.post(DEFECT_NOTICE_CREATE_ENDPOINT, formData, {
    withCredentials: true,
  });
  if (response.status === 201) {
    return;
  }
  throw new Error('Failed to create defect notice');
}

export async function initializeImpersonation(data: InitiateImpersonation): Promise<string> {
  const response = await axios.post(IMPERSONATE_INIT_ENDPOINT, data);
  if (response.status === 200) {
    return response.data.url;
  }
  throw new Error('Failed to initialize impersonation session');
}

/**
 * Submits the auth nonce and, on success, sets the received session ID as active and uses it to authenticate
 * requests.
 */
export async function submitImpersonatorAuthNonce(nonce: string): Promise<void> {
  const response = await axios.post(IMPERSONATE_LOGIN_ENDPOINT, { nonce });
  if (response.status === 200 && response.data.sessionId) {
    setSessionId(response.data.sessionId);
  } else {
    throw new Error('Failed to fetch session ID');
  }
}
